import { useEffect, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { StatTile } from '@/components/StatTile';
import { instagramService, IgAccountInfo, IgMediaItem, IgMediaListResponse, IgInsightsResponse, getMediaInsight } from '@/services/instagram.service';
import { InstagramPostInsightsModal } from '@/components/facebook/InstagramPostInsightsModal';
import { Heart, MessageCircle, Eye, Bookmark, Activity, RefreshCw, ExternalLink, AlertCircle, Plus, Image as ImageIcon, Camera, LogOut, Repeat } from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { facebookAdAccountService } from '@/services/facebook-ad-account.service';
import { IgAccountSwitchModal } from '@/components/facebook/IgAccountSwitchModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';

export default function InstagramOverview() {
  const [account, setAccount] = useState<IgAccountInfo | null>(null);
  const [media, setMedia] = useState<IgMediaListResponse | null>(null);
  const [insights, setInsights] = useState<IgInsightsResponse | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insightsMedia, setInsightsMedia] = useState<IgMediaItem | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = async (force = false) => {
    setRefreshing(true);
    setError(null);
    try {
      const [a, m, i] = await Promise.allSettled([
        instagramService.account(force),
        instagramService.media(force),
        instagramService.insights(force, 30),
      ]);
      if (a.status === 'fulfilled') {
        setAccount(a.value);
      } else if (!account) {
        const reason: any = a.reason;
        if (reason?.response?.data?.code === 'INSTAGRAM_NOT_CONNECTED') {
          setError('NOT_CONNECTED');
        } else {
          setError(reason?.response?.data?.message ?? 'Failed to load Instagram profile');
        }
      }
      if (m.status === 'fulfilled') setMedia(m.value);
      if (i.status === 'fulfilled') setInsights(i.value);
    } finally {
      setRefreshing(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, []);

  /** Fetch the next page of media using Meta's `after` cursor and append. */
  const loadMore = async () => {
    const cursor = media?.paging?.cursors?.after;
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const next = await instagramService.media(false, undefined, cursor);
      setMedia((prev) => prev ? {
        ...next,
        data: [...prev.data, ...next.data],
      } : next);
    } finally {
      setLoadingMore(false);
    }
  };

  /** Another IG account was linked — drop everything from the old one and reload. */
  const onSwitched = () => {
    setSwitching(false);
    setAccount(null); setMedia(null); setInsights(null); setError(null);
    load(true);
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await facebookAdAccountService.disconnectInstagram();
      toast.success('Instagram disconnected');
      setConfirmDisconnect(false);
      setAccount(null); setMedia(null); setInsights(null);
      setError('NOT_CONNECTED');
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Could not disconnect');
    } finally { setDisconnecting(false); }
  };

  if (error === 'NOT_CONNECTED') {
    return (
      <>
        <Topbar title="Instagram" breadcrumb="Instagram / Overview" />
        <div className="w-full px-7 py-7">
          <div className="mx-auto max-w-xl rounded-2xl border border-border bg-surface p-8 text-center shadow-soft">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold-gradient-soft text-primary">
              <Camera size={28} />
            </span>
            <h3 className="mt-4 font-display text-lg font-bold text-text-primary">No Instagram account connected</h3>
            <p className="mt-1.5 text-sm text-text-muted">
              Pick the Instagram professional account this workspace should manage — posts, insights and campaigns all read from it.
            </p>
            <div className="mt-5 flex justify-center">
              <Button iconLeft={<Camera size={14} />} onClick={() => setSwitching(true)}>Connect Instagram account</Button>
            </div>
            <p className="mt-4 text-2xs text-text-muted">
              Only accounts linked to a Facebook Page on your Meta connection appear. Link one in{' '}
              <a href="https://business.facebook.com" target="_blank" rel="noreferrer" className="text-primary underline">Meta Business Suite</a>
              {' '}or manage the Meta connection under{' '}
              <Link to="/facebook/accounts" className="text-primary underline">Facebook Ads → Accounts</Link>.
            </p>
          </div>
        </div>
        <IgAccountSwitchModal open={switching} currentId={null} onClose={() => setSwitching(false)} onSwitched={onSwitched} />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Topbar title="Instagram" breadcrumb="Instagram / Overview" />
        <div className="px-7 py-7 w-full">
          <div className="flex items-start gap-3 rounded-xl border border-danger/30 bg-danger-soft p-4">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-danger" />
            <p className="text-sm text-danger">{error}</p>
          </div>
          <Button className="mt-4" onClick={() => load(true)}>Try again</Button>
        </div>
      </>
    );
  }

  if (!account) {
    return (
      <>
        <Topbar title="Instagram" breadcrumb="Instagram / Overview" />
        <div className="px-7 py-7 w-full space-y-4">
          <div className="skeleton h-36 rounded-2xl" />
          <div className="skeleton h-64 rounded-2xl" />
          <div className="grid grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <div key={i} className="skeleton aspect-square rounded-2xl" />)}
          </div>
        </div>
      </>
    );
  }

  const chartRows = buildChartRows(insights);
  const totals = buildTotals(insights);

  return (
    <>
      <Topbar
        title={`@${account.username}`}
        breadcrumb="Instagram / Overview"
        right={
          <div className="flex items-center gap-2">
            <Link to="/instagram/posts/new">
              <Button iconLeft={<Plus size={14} />}>New Post</Button>
            </Link>
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:text-text-primary disabled:opacity-50 transition-colors"
              onClick={() => load(true)}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
            </button>
          </div>
        }
      />

      <div className="px-7 py-7 w-full space-y-5">
        {/* Profile header — laid out the way Instagram's own profile page is:
            avatar left, then username + actions, the three counters, and the
            name / bio / link underneath. */}
        <Card>
          <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:gap-8 lg:gap-12">
            <div className="shrink-0 self-center sm:self-start sm:pl-4">
              <span className="block rounded-full bg-[conic-gradient(from_180deg,#f9ce34,#ee2a7b,#6228d7,#f9ce34)] p-[3px]">
                <span className="block rounded-full bg-surface p-[3px]">
                  {account.profile_picture_url ? (
                    <img src={account.profile_picture_url} alt={account.username} className="h-28 w-28 rounded-full object-cover sm:h-36 sm:w-36" />
                  ) : (
                    <span className="flex h-28 w-28 items-center justify-center rounded-full bg-gold-gradient-soft font-display text-3xl font-bold text-primary sm:h-36 sm:w-36">
                      {account.username.slice(0, 2).toUpperCase()}
                    </span>
                  )}
                </span>
              </span>
            </div>

            <div className="min-w-0 flex-1 space-y-4">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <h2 className="truncate text-xl font-normal text-text-primary">{account.username}</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="secondary" iconLeft={<Repeat size={13} />} onClick={() => setSwitching(true)}>Switch account</Button>
                  <a href={`https://www.instagram.com/${account.username}/`} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="secondary" iconRight={<ExternalLink size={12} />}>View on Instagram</Button>
                  </a>
                  <button
                    type="button"
                    onClick={() => setConfirmDisconnect(true)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-medium text-text-muted transition-colors hover:bg-danger-soft hover:text-danger"
                    title="Disconnect this Instagram account from the CRM"
                  >
                    <LogOut size={13} />Disconnect
                  </button>
                </div>
              </div>

              <dl className="flex items-center gap-8 text-sm">
                {[
                  { label: 'posts', value: account.media_count },
                  { label: 'followers', value: account.followers_count },
                  { label: 'following', value: account.follows_count },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-baseline gap-1.5">
                    <dt className="sr-only">{label}</dt>
                    <dd className="font-semibold tabular-nums text-text-primary">{value?.toLocaleString() ?? '—'}</dd>
                    <span className="text-text-secondary">{label}</span>
                  </div>
                ))}
              </dl>

              <div className="max-w-xl text-sm">
                <div className="font-semibold text-text-primary">{account.name || account.username}</div>
                {account.biography && <p className="mt-0.5 whitespace-pre-line leading-relaxed text-text-secondary">{account.biography}</p>}
                {account.website && (
                  <a href={account.website} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 font-medium text-[#00376b] hover:underline dark:text-[#e0f1ff]">
                    {account.website.replace(/^https?:\/\//, '')}
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </Card>

        <IgAccountSwitchModal open={switching} currentId={account.id} onClose={() => setSwitching(false)} onSwitched={onSwitched} />
        <ConfirmDialog
          open={confirmDisconnect}
          danger
          title={`Disconnect @${account.username}?`}
          message="The Instagram module stops reading this account until you connect one again. Nothing is changed on Instagram itself, and campaigns already on Meta keep running."
          onConfirm={() => { if (!disconnecting) disconnect(); }}
          onCancel={() => setConfirmDisconnect(false)}
        />

        {/* Insights. Meta serves this edge with `metric_type=total_value`, so
            every metric is a 30-day aggregate with no per-day series — the
            line chart only renders on the rare metric that still carries
            `values[]`. Totals are shown either way so the section is never
            blank when there IS data. */}
        <Card>
          <SectionHeader
            title="Account insights — last 30 days"
            icon={<Activity size={16} />}
            subtitle={insights?.error ? `⚠ Some metrics unavailable: ${insights.error}` : undefined}
          />
          {totals.length > 0 && (
            <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-5">
              {totals.map((t) => (
                <StatTile key={t.name} label={t.label} value={t.value.toLocaleString()} accent={t.accent} />
              ))}
            </div>
          )}
          {chartRows.length > 0 ? (
            <div style={{ height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={chartRows}>
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="impressions" stroke="var(--color-primary)" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="reach" stroke="var(--color-info)" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="profile_views" stroke="var(--color-success)" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : totals.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Activity size={14} />
              No insights data available for this period.
            </div>
          ) : (
            <p className="text-2xs text-text-muted">
              Meta reports these as 30-day totals, not a daily series — per-day trends aren&apos;t available on this edge.
            </p>
          )}
        </Card>

        {/* Recent media grid */}
        <div>
          <SectionHeader
            title="Recent posts"
            icon={<ImageIcon size={16} />}
            action={
              <Link to="/instagram/posts" className="text-xs font-semibold text-primary hover:underline">
                See all posts
              </Link>
            }
          />
          {!media || media.data.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-surface p-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                <ImageIcon size={24} />
              </div>
              <div className="text-sm text-text-muted">No posts yet.</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-4 lg:grid-cols-4">
                {media.data.map((m) => (
                  <MediaCard key={m.id} media={m} onClick={() => setInsightsMedia(m)} />
                ))}
              </div>
              {media.paging?.next && (
                <div className="mt-4 flex justify-center">
                  <Button variant="secondary" loading={loadingMore} onClick={loadMore}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <InstagramPostInsightsModal
        open={!!insightsMedia}
        onClose={() => setInsightsMedia(null)}
        media={insightsMedia}
        onDeleted={(deletedId) => {
          // Optimistically remove the deleted post from the grid so the user
          // doesn't have to wait for a full refresh; then force-refresh the
          // Meta cache in the background.
          setMedia((prev) => prev ? {
            ...prev,
            data: prev.data.filter((m) => m.id !== deletedId),
          } : prev);
          load(true).catch(() => undefined);
        }}
      />
    </>
  );
}

function buildChartRows(insights: IgInsightsResponse | null): Array<{ day: string; impressions: number; reach: number; profile_views: number }> {
  // Meta returns a mix of shapes on this edge: time-series metrics carry
  // `values[]`, but newer/aggregate ones carry `total_value` and no `values`
  // at all. Iterating blindly threw "metric.values is not iterable" and took
  // the whole page down, so every level is treated as optional here.
  if (!insights || !Array.isArray(insights.data) || insights.data.length === 0) return [];
  const days = new Map<string, { day: string; impressions: number; reach: number; profile_views: number }>();
  for (const metric of insights.data) {
    if (!metric || !['impressions', 'reach', 'profile_views'].includes(metric.name)) continue;
    if (!Array.isArray(metric.values)) continue;
    for (const v of metric.values) {
      if (!v) continue;
      if (!v.end_time) continue;
      const day = v.end_time.slice(0, 10);
      const row = days.get(day) ?? { day, impressions: 0, reach: 0, profile_views: 0 };
      const value = typeof v.value === 'number' ? v.value : 0;
      (row as any)[metric.name] = value;
      days.set(day, row);
    }
  }
  return Array.from(days.values()).sort((a, b) => a.day.localeCompare(b.day));
}

type TotalTile = { name: string; label: string; value: number; accent: 'gold' | 'green' | 'blue' | 'amber' | 'red' };

const TOTAL_META: Record<string, { label: string; accent: TotalTile['accent'] }> = {
  reach:              { label: 'Reach',            accent: 'blue' },
  accounts_engaged:   { label: 'Accounts engaged', accent: 'green' },
  total_interactions: { label: 'Interactions',     accent: 'gold' },
  profile_views:      { label: 'Profile views',    accent: 'amber' },
  website_clicks:     { label: 'Website clicks',   accent: 'red' },
  views:              { label: 'Views',            accent: 'blue' },
};

/** Pull the 30-day aggregates Meta returns under `total_value`. */
function buildTotals(insights: IgInsightsResponse | null): TotalTile[] {
  if (!insights || !Array.isArray(insights.data)) return [];
  const out: TotalTile[] = [];
  for (const metric of insights.data) {
    const total = metric?.total_value?.value;
    if (typeof total !== 'number') continue;
    const meta = TOTAL_META[metric.name] ?? { label: metric.name.replace(/_/g, ' '), accent: 'gold' as const };
    out.push({ name: metric.name, label: meta.label, value: total, accent: meta.accent });
  }
  return out;
}

const MediaCard: React.FC<{ media: IgMediaItem; onClick: () => void }> = ({ media, onClick }) => {
  // Prefer ImageKit mirror — IG's signed CDN URLs expire in a few hours.
  const thumb = media.imagekit_url || media.thumbnail_url || media.media_url;
  const reach = getMediaInsight(media, 'reach');
  const impressions = getMediaInsight(media, 'impressions');
  const saved = getMediaInsight(media, 'saved');

  return (
    <button
      type="button"
      onClick={onClick}
      className="group block w-full text-left overflow-hidden rounded-2xl border border-border bg-surface shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-elevated focus:outline-none focus:ring-2 focus:ring-primary"
    >
      <div className="relative aspect-square bg-surface-alt">
        {thumb && (
          <img src={thumb} alt="" className="h-full w-full object-cover" />
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center gap-4 bg-black/60 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <span className="flex items-center gap-1 text-white text-sm font-semibold">
            <Heart size={14} className="fill-current" />
            {(media.like_count ?? 0).toLocaleString()}
          </span>
          <span className="flex items-center gap-1 text-white text-sm font-semibold">
            <MessageCircle size={14} />
            {(media.comments_count ?? 0).toLocaleString()}
          </span>
        </div>
        <div className="absolute top-2 right-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] text-white">
          {media.media_type}{media.media_product_type ? ` · ${media.media_product_type}` : ''}
        </div>
      </div>

      <div className="p-3">
        {media.caption && (
          <p className="mb-1.5 line-clamp-2 text-xs text-text-primary">{media.caption}</p>
        )}
        <div className="text-[10px] text-text-muted">
          {media.timestamp ? new Date(media.timestamp).toLocaleDateString() : ''}
        </div>
        {(reach !== null || saved !== null || impressions !== null) && (
          <div className="mt-2 flex gap-3 text-xs text-text-muted">
            {reach !== null && (
              <span className="flex items-center gap-1">
                <Eye size={11} />
                {reach.toLocaleString()}
              </span>
            )}
            {saved !== null && (
              <span className="flex items-center gap-1">
                <Bookmark size={11} />
                {saved.toLocaleString()}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
};
